import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { useFormContext } from 'react-hook-form';
import moment, { Moment } from 'moment';
import debounce from 'lodash/debounce';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined';
import TodayOutlinedIcon from '@mui/icons-material/TodayOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { PersonDTO } from '@behavior/inventories/dto/PersonDTO';
import { getHsePersonAutocomplete } from '@behavior/persons/hseAutocompleteApi';
import { backendDateFormat } from '@src/Dates';
import useUser from '@behavior/init/getUserV2';

type Props = {
  hseUnit?: PersonDTO;
  setHseUnit?: (hseUnit?: PersonDTO) => void;
  signatureDate?: string;
  setSignatureDate?: (signatureDate?: string) => void;
  disabled?: boolean;
  showPersonalData?: boolean;
};

const HSE_FIELD = 'hseUnit';
const HSE_DATE_FIELD = 'hseDate';

// Neutral grey accent. The card sits inside a section that already announces
// its purpose ("HSE Unit"), so the chrome stays quiet — colour earns its way
// in only on the org-unit chip (blue) and on the explicit done/pending pills.
const ACCENT = '#6b7280';
const ACCENT_TINT = 'rgba(107, 114, 128, 0.10)';
const SURFACE = '#fdfdfc';
const BORDER = '#e7e5e0';
const BORDER_STRONG = '#1c1917';
const TEXT_MUTED = '#78716c';

// Department chip uses a confident corporate blue so it stands out against
// the teal HSE accent and reads as a label rather than another action.
const CHIP_BLUE = '#1d4ed8';
const CHIP_BLUE_TINT = '#dbeafe';

const DISPLAY_DATE_FORMAT = 'DD-MM-YYYY';

const maskPrivate = (s: string): string => s.replace(/[a-z]/g, 'x');

const displayName = (p: PersonDTO | undefined, showPersonalData: boolean): string => {
  if (!p) return '';
  const raw = p.name || [p.firstName, p.lastName].filter(Boolean).join(' ');
  return showPersonalData ? raw : maskPrivate(raw);
};

// `HSE-TS-CS` → `HSE-TS`. Department + group only; the unit suffix is dropped
// to keep the chip and the suggestion row scannable.
const departmentAndGroup = (orgUnit?: string): string | undefined => {
  if (!orgUnit) return undefined;
  return orgUnit.split('-').slice(0, 2).join('-');
};

const chipSx = {
  height: 20,
  borderRadius: 0.5,
  fontSize: '0.6875rem',
  fontWeight: 600,
  letterSpacing: '0.02em',
  color: CHIP_BLUE,
  bgcolor: CHIP_BLUE_TINT,
  border: `1px solid ${CHIP_BLUE}`,
  '& .MuiChip-label': { px: 0.75 },
};

const inputFrameSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: '#fff',
    pr: 0.75,
    '& fieldset': { borderColor: BORDER },
    '&:hover fieldset': { borderColor: BORDER_STRONG },
    '&.Mui-focused fieldset': { borderColor: ACCENT, borderWidth: 1 },
  },
};

const HseUnitCard = ({
  hseUnit,
  setHseUnit,
  signatureDate,
  setSignatureDate,
  disabled,
  showPersonalData = true,
}: Props) => {
  const { t } = useTranslation();
  const { register, setValue, trigger } = useFormContext();
  const { user } = useUser();

  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<PersonDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [dateOpen, setDateOpen] = useState(false);

  // Derive whether the logged-in user is themselves in HSE. We trust the DB
  // value (org_unit / actualDepartment), not anything the client could spoof.
  const meAsHse: PersonDTO | undefined = useMemo(() => {
    if (!user?.person) return undefined;
    const dept =
      user.actualDepartment || (user.person.orgUnit ?? '').split('-')[0] || '';
    return dept.toUpperCase() === 'HSE' ? user.person : undefined;
  }, [user]);
  const canPickMe = !!meAsHse && !hseUnit;

  useEffect(() => {
    setValue(HSE_FIELD, hseUnit);
    trigger([HSE_FIELD]);
  }, [hseUnit]);

  useEffect(() => {
    setValue(HSE_DATE_FIELD, signatureDate);
    trigger([HSE_DATE_FIELD]);
  }, [signatureDate]);

  const fetchOptions = useMemo(
    () =>
      debounce((query: string, done: (results: PersonDTO[]) => void) => {
        if (!query || query.trim().length < 2) {
          done([]);
          return;
        }
        getHsePersonAutocomplete(query)
          .then(done)
          .catch(() => done([]));
      }, 250),
    []
  );

  useEffect(() => {
    setLoading(true);
    fetchOptions(inputValue, results => {
      setOptions(results);
      setLoading(false);
    });
    return () => {
      fetchOptions.cancel();
    };
  }, [inputValue, fetchOptions]);

  const handlePersonChange = (_: unknown, value: PersonDTO | null) => {
    if (setHseUnit) setHseUnit(value || undefined);
  };

  const handleDateChange = (next: Moment | null) => {
    if (!setSignatureDate) return;
    setSignatureDate(next && next.isValid() ? next.format(backendDateFormat) : undefined);
  };

  const pickMe = () => {
    if (meAsHse && setHseUnit) setHseUnit(meAsHse);
  };

  const clearPerson = () => {
    if (setHseUnit) setHseUnit(undefined);
  };

  const pickToday = () => {
    if (setSignatureDate) setSignatureDate(moment().format(backendDateFormat));
  };

  const clearDate = () => {
    if (setSignatureDate) setSignatureDate(undefined);
  };

  const actionIconSx = {
    p: 0.5,
    color: TEXT_MUTED,
    transition: 'color 120ms ease, background-color 120ms ease',
    '&:hover': { color: ACCENT, bgcolor: ACCENT_TINT },
    '&:focus-visible': { outline: `1px solid ${ACCENT}`, outlineOffset: 1 },
  };

  // Seed the options with the current value so MUI doesn't warn when options
  // haven't been fetched yet (e.g. on form load with a pre-existing hseUnit).
  const effectiveOptions = hseUnit
    ? [hseUnit, ...options.filter(o => o.id !== hseUnit.id)]
    : options;

  const selectedChipLabel = departmentAndGroup(hseUnit?.orgUnit);

  return (
    <Paper
      variant="outlined"
      sx={{
        m: 1,
        borderColor: BORDER,
        borderRadius: 1,
        bgcolor: SURFACE,
      }}
    >
      <Box sx={{ pl: 1.75, pr: 1.5, py: 1.25 }}>
        <Typography
          component="div"
          sx={{
            textTransform: 'uppercase',
            letterSpacing: '0.12em',
            fontSize: '0.6875rem',
            fontWeight: 600,
            color: TEXT_MUTED,
            mb: 0.75,
          }}
        >
          {t('traceability.form.details.page.container.hse.unit')}
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems="stretch">
          <Box sx={{ flex: 2, minWidth: 0 }}>
            <Autocomplete<PersonDTO, false, false, false>
              value={hseUnit ?? null}
              onChange={handlePersonChange}
              inputValue={inputValue}
              onInputChange={(_, v) => setInputValue(v)}
              options={effectiveOptions}
              loading={loading}
              disabled={disabled}
              popupIcon={null}
              size="small"
              isOptionEqualToValue={(o, v) => o?.id === v?.id}
              getOptionLabel={p => displayName(p, showPersonalData)}
              filterOptions={x => x}
              noOptionsText={
                inputValue.trim().length < 2
                  ? t('traceability.form.details.page.container.hse.unit.typeToSearch')
                  : t('traceability.form.details.page.container.hse.unit.noMatches')
              }
              slotProps={{
                paper: {
                  sx: {
                    mt: 0.5,
                    borderRadius: 1,
                    border: `1px solid ${BORDER}`,
                  },
                },
              }}
              renderOption={(props, option) => {
                const chipLabel = departmentAndGroup(option.orgUnit);
                return (
                  <Box
                    component="li"
                    {...props}
                    key={option.id}
                    sx={{ py: 0.5, px: 1.25, gap: 1 }}
                  >
                    <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>
                      {displayName(option, showPersonalData)}
                    </Typography>
                    {chipLabel && <Chip label={chipLabel} size="small" sx={chipSx} />}
                  </Box>
                );
              }}
              renderInput={params => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder={t('traceability.form.details.page.container.hse.unit.placeholder')}
                  inputRef={register(HSE_FIELD).ref}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <InputAdornment position="end" sx={{ mr: -0.25, gap: 0.75 }}>
                        {selectedChipLabel && (
                          <Chip label={selectedChipLabel} size="small" sx={chipSx} />
                        )}
                        {hseUnit ? (
                          <Tooltip
                            title={t(
                              'traceability.form.details.page.container.hse.unit.remove'
                            )}
                          >
                            <IconButton
                              size="small"
                              onMouseDown={e => e.preventDefault()}
                              onClick={clearPerson}
                              disabled={disabled}
                              sx={actionIconSx}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : canPickMe ? (
                          <Tooltip
                            title={t('traceability.form.details.page.container.hse.unit.me')}
                          >
                            <IconButton
                              size="small"
                              onMouseDown={e => e.preventDefault()}
                              onClick={pickMe}
                              disabled={disabled}
                              sx={actionIconSx}
                            >
                              <PersonAddOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : null}
                      </InputAdornment>
                    ),
                  }}
                  sx={inputFrameSx}
                />
              )}
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: 168, maxWidth: 240 }}>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker
                format={DISPLAY_DATE_FORMAT}
                value={signatureDate ? moment(signatureDate, backendDateFormat) : null}
                onChange={handleDateChange}
                disabled={disabled}
                open={dateOpen}
                onOpen={() => setDateOpen(true)}
                onClose={() => setDateOpen(false)}
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                    placeholder: DISPLAY_DATE_FORMAT.toLowerCase(),
                    InputProps: {
                      endAdornment: (
                        <InputAdornment position="end" sx={{ mr: -0.25, gap: 0.25 }}>
                          {signatureDate ? (
                            <Tooltip
                              title={t(
                                'traceability.form.details.page.container.hse.unit.remove'
                              )}
                            >
                              <IconButton
                                size="small"
                                onClick={clearDate}
                                disabled={disabled}
                                sx={actionIconSx}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Tooltip
                              title={t(
                                'traceability.form.details.page.container.hse.unit.today'
                              )}
                            >
                              <IconButton
                                size="small"
                                onClick={pickToday}
                                disabled={disabled}
                                sx={actionIconSx}
                              >
                                <TodayOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip
                            title={t(
                              'traceability.form.details.page.container.hse.unit.openCalendar'
                            )}
                          >
                            <IconButton
                              size="small"
                              onClick={() => setDateOpen(true)}
                              disabled={disabled}
                              sx={actionIconSx}
                            >
                              <CalendarMonthOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </InputAdornment>
                      ),
                    },
                    sx: inputFrameSx,
                  },
                  popper: {
                    sx: {
                      '& .MuiPickersDay-root.Mui-selected': {
                        bgcolor: ACCENT,
                        '&:hover': { bgcolor: ACCENT },
                      },
                      '& .MuiPickersDay-root:focus.Mui-selected': { bgcolor: ACCENT },
                    },
                  },
                }}
              />
            </LocalizationProvider>
          </Box>
        </Stack>
      </Box>
    </Paper>
  );
};

const mapStateToProps = (state: any) => ({
  showPersonalData: state.dataPrivacy.showPersonalData,
});

export default connect(mapStateToProps, null)(HseUnitCard);
